import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Loader2,
  Upload,
  Target,
  ExternalLink,
  Trash2,
  AlertOctagon,
  CheckCircle2,
  RefreshCw,
  FileText,
  Mail,
  Send,
  Check,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  QUEUED: "bg-zinc-200 text-zinc-800",
  RUNNING: "bg-blue-700 text-white",
  COMPLETED: "bg-emerald-600 text-white",
  FAILED: "bg-red-600 text-white",
};

function StatusBadge({ status }) {
  return (
    <span
      className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        STATUS_STYLES[status] || "bg-zinc-200"
      }`}
      data-testid={`hunt-status-${status}`}
    >
      {status}
    </span>
  );
}

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="w-full h-2 bg-zinc-200 rounded-sm overflow-hidden">
      <div
        className="h-full bg-blue-700 transition-all duration-300"
        style={{ width: `${pct}%` }}
        data-testid="hunt-progress-bar"
      />
    </div>
  );
}

export default function CompetitorHunt() {
  const [file, setFile] = useState(null);
  const [tag, setTag] = useState("accessibe");
  const [minIssues, setMinIssues] = useState(5);
  const [uploading, setUploading] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null); // { job, results }
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Map<auditId, "sending"|"sent"|"error">
  const [sendingState, setSendingState] = useState({});
  // Stage-4 · multi-select for bulk-send
  const [selectedResultIds, setSelectedResultIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  // Stage-4b · toggle to reveal non-qualified (low-count / failed) rows.
  // Defaults to false so the on-screen count reconciles with `job.qualified`.
  const [showNonQualified, setShowNonQualified] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadJobs = async () => {
    try {
      const r = await api.get("/v1/competitor-hunt/jobs");
      setJobs(r.data || []);
      if (!selectedJobId && r.data?.[0]) setSelectedJobId(r.data[0].id);
    } catch {
      // silent
    }
  };

  const loadDetail = async (jobId, opts = {}) => {
    if (!jobId) return;
    setLoadingDetail(true);
    try {
      const params = {};
      if (opts.showNonQualified ?? showNonQualified) {
        params.include_low_count = "true";
        params.include_failed = "true";
      }
      const r = await api.get(`/v1/competitor-hunt/jobs/${jobId}`, { params });
      setJobDetail(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load job");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (selectedJobId) loadDetail(selectedJobId);
    setSelectedResultIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId, showNonQualified]);

  // Poll while any job is in flight OR the selected one is.
  useEffect(() => {
    const anyActive = jobs.some(
      (j) => j.status === "RUNNING" || j.status === "QUEUED"
    );
    const selectedActive =
      jobDetail?.job?.status === "RUNNING" ||
      jobDetail?.job?.status === "QUEUED";
    if (!anyActive && !selectedActive) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      loadJobs();
      if (selectedJobId) loadDetail(selectedJobId);
    }, 4000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobs, jobDetail, selectedJobId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!file) {
      toast.error("Choose a .csv or .txt file first");
      return;
    }
    const lc = file.name.toLowerCase();
    if (!lc.endsWith(".csv") && !lc.endsWith(".txt")) {
      toast.error("Only .csv and .txt files supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/v1/competitor-hunt/upload", fd, {
        params: {
          competitor_tag: tag.trim() || "accessibe",
          min_issues: Math.max(1, Math.min(100, parseInt(minIssues) || 5)),
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Queued ${r.data.total_urls} URLs for audit`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSelectedJobId(r.data.id);
      await loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm(
      "Delete this job? Audit results stay (use Purge to also remove qualified audits)."
    )) return;
    try {
      await api.delete(`/v1/competitor-hunt/jobs/${jobId}`);
      toast.success("Job deleted");
      setSelectedJobId(null);
      setJobDetail(null);
      await loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const purgeJob = async (jobId) => {
    if (!window.confirm(
      "Delete job AND all qualified audits it produced? Saved audits are preserved. This cannot be undone."
    )) return;
    try {
      const r = await api.delete(`/v1/competitor-hunt/jobs/${jobId}`, {
        params: { purge_audits: true },
      });
      toast.success(`Job purged · ${r.data.deleted_audits} audits removed`);
      setSelectedJobId(null);
      setJobDetail(null);
      await loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    }
  };

  // Compile + send the pitch PDF for a single qualified result. The audit
  // doc has already been generated by the hunt worker; if pitch_dossier is
  // missing we trigger generation server-side first via /audits/{id}/pitch.
  const sendPitchInline = async (audit) => {
    if (!audit.discovered_email) {
      toast.error("No discovered email on this audit — open it in Site Auditor to pick one manually.");
      return;
    }
    setSendingState((s) => ({ ...s, [audit.id]: "sending" }));
    try {
      // Ensure a dossier exists (cheap no-op if it does).
      if (!audit.pitch_dossier) {
        await api.post(`/audits/${audit.id}/pitch`);
      }
      const r = await api.post("/v1/outreach/send-pitch", {
        audit_id: audit.id,
        to_email: audit.discovered_email,
      });
      if (r.data?.sent) {
        setSendingState((s) => ({ ...s, [audit.id]: "sent" }));
        toast.success(
          r.data.mode === "resend"
            ? `Sent via Resend → ${audit.discovered_email}`
            : r.data.mode === "dry_run"
            ? `Dry-run send (no email transport configured)`
            : `Sent → ${audit.discovered_email}`,
        );
        // Refresh detail so pitch_status badge updates.
        await loadDetail(selectedJobId);
      } else {
        setSendingState((s) => ({ ...s, [audit.id]: "error" }));
        toast.error(r.data?.message || "Send failed");
      }
    } catch (err) {
      setSendingState((s) => ({ ...s, [audit.id]: "error" }));
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  const job = jobDetail?.job;
  const results = jobDetail?.results || [];
  const pctDone = job && job.total_urls > 0
    ? Math.round((job.completed / job.total_urls) * 100)
    : 0;

  // Stage-4 · selection helpers
  const isResultEligible = (r) => !!r.discovered_email && r.pitch_status !== "PITCHED" && sendingState[r.id] !== "sent";
  const eligibleResultIds = results.filter(isResultEligible).map((r) => r.id);
  const ineligibleCount = results.length - eligibleResultIds.length;
  const selectedEligible = eligibleResultIds.filter((id) => selectedResultIds.has(id));
  const allEligibleSelected = eligibleResultIds.length > 0 && selectedEligible.length === eligibleResultIds.length;
  const someEligibleSelected = selectedEligible.length > 0 && !allEligibleSelected;
  const toggleResultSelect = (id) => setSelectedResultIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleResultSelectAll = () => {
    setSelectedResultIds((prev) => {
      if (allEligibleSelected) {
        const next = new Set(prev);
        eligibleResultIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      eligibleResultIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearResultSelection = () => setSelectedResultIds(new Set());

  const bulkSendPitches = async () => {
    const ids = Array.from(selectedResultIds).filter((id) => eligibleResultIds.includes(id));
    if (ids.length === 0) return;
    setBulkSending(true);
    let sent = 0, failed = 0;
    for (const id of ids) {
      const audit = results.find((r) => r.id === id);
      if (!audit) continue;
      setSendingState((s) => ({ ...s, [id]: "sending" }));
      try {
        if (!audit.pitch_dossier) {
          try { await api.post(`/audits/${id}/pitch`); } catch { /* fall through — /send-pitch will surface */ }
        }
        const r = await api.post("/v1/outreach/send-pitch", { audit_id: id, to_email: audit.discovered_email });
        if (r.data?.sent) {
          setSendingState((s) => ({ ...s, [id]: "sent" }));
          sent += 1;
        } else {
          setSendingState((s) => ({ ...s, [id]: "error" }));
          failed += 1;
        }
      } catch {
        setSendingState((s) => ({ ...s, [id]: "error" }));
        failed += 1;
      }
    }
    setBulkSending(false);
    setSelectedResultIds(new Set());
    if (sent > 0) toast.success(`Bulk send · ${sent} sent${failed ? `, ${failed} failed` : ""}`);
    else toast.error(`Bulk send failed · 0/${ids.length} succeeded`);
    await loadDetail(selectedJobId);
  };

  return (
    <div data-testid="competitor-hunt-page">
      <PageHeader
        eyebrow="Module 06 · Competitor Hunt"
        title="Snipe competitor customers in bulk"
        description="Upload a CSV/TXT of AccessiBe (or any overlay vendor's) customer domains. We bulk-audit them and surface only the ones still leaking enough WCAG violations to make a credible pitch."
        testid="competitor-hunt-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-6">
        {/* Left column — upload + jobs list */}
        <div>
          <form
            onSubmit={submit}
            className="border border-zinc-950 p-5 bg-white"
            data-testid="competitor-hunt-form"
          >
            <div className="label-xs mb-1">New bulk hunt</div>

            <Label className="text-xs font-bold">URL list (.csv or .txt)</Label>
            <label
              className="mt-1 flex items-center gap-2 px-3 py-3 border-2 border-dashed border-zinc-400 hover:border-blue-600 hover:bg-blue-50/40 cursor-pointer transition-colors"
              data-testid="competitor-hunt-dropzone"
            >
              <Upload className="w-4 h-4 text-zinc-600" />
              <span className="text-xs font-mono truncate flex-1">
                {file ? file.name : "Choose file…"}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="competitor-hunt-file-input"
              />
            </label>
            <p className="mt-1 text-[10px] text-zinc-500">
              .txt: one URL per line · .csv: first column = URL (header row ignored).
            </p>

            <Label className="text-xs font-bold mt-4 block">Competitor tag</Label>
            <Input
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="accessibe"
              className="rounded-sm h-10 mt-1 font-mono text-sm"
              data-testid="competitor-hunt-tag-input"
            />

            <Label className="text-xs font-bold mt-3 block">
              Min issues threshold
              <span className="ml-2 font-mono font-normal text-zinc-500">
                (audits below this stay in the DB but are hidden by default)
              </span>
            </Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={minIssues}
              onChange={(e) => setMinIssues(e.target.value)}
              className="rounded-sm h-10 mt-1 font-mono text-sm"
              data-testid="competitor-hunt-min-issues-input"
            />

            <Button
              type="submit"
              disabled={uploading || !file}
              className="w-full mt-5 h-11 rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider disabled:opacity-40"
              data-testid="competitor-hunt-submit-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Target className="w-4 h-4 mr-2" /> Start Hunt
                </>
              )}
            </Button>
          </form>

          {/* Jobs list */}
          <div className="mt-4 border border-zinc-200" data-testid="hunt-jobs-list">
            <div className="bg-zinc-100 px-3 py-1.5 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">
                Hunt Jobs
              </span>
              <button
                type="button"
                onClick={loadJobs}
                title="Refresh"
                className="text-zinc-500 hover:text-zinc-950"
                data-testid="hunt-jobs-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <ul className="max-h-[420px] overflow-auto">
              {jobs.length === 0 && (
                <li className="px-3 py-6 text-center text-xs text-zinc-500">
                  No hunts yet. Upload a list to get started.
                </li>
              )}
              {jobs.map((j) => {
                const pct = j.total_urls > 0
                  ? Math.round((j.completed / j.total_urls) * 100)
                  : 0;
                return (
                  <li
                    key={j.id}
                    className={`border-b border-zinc-100 ${
                      selectedJobId === j.id
                        ? "bg-zinc-100 border-l-4 border-l-blue-700"
                        : ""
                    }`}
                  >
                    <button
                      onClick={() => setSelectedJobId(j.id)}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-50"
                      data-testid={`hunt-job-row-${j.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <span className="text-xs font-mono truncate flex-1">
                          {j.filename}
                        </span>
                        <StatusBadge status={j.status} />
                      </div>
                      <div className="mt-1.5">
                        <ProgressBar completed={j.completed} total={j.total_urls} />
                      </div>
                      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500 font-mono">
                        <span>
                          {j.completed}/{j.total_urls} · {pct}%
                        </span>
                        <span>
                          <span className="text-emerald-700 font-bold">
                            {j.qualified}
                          </span>{" "}
                          qualified · tag:{j.competitor_tag}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Right column — selected job detail */}
        <div>
          {!job && !loadingDetail && (
            <div className="border border-zinc-950 p-12 text-center text-zinc-500">
              <Target className="w-8 h-8 mx-auto mb-3 text-zinc-400" />
              Upload a list to start a hunt, or select an existing job.
            </div>
          )}

          {loadingDetail && !job && (
            <div className="border border-zinc-950 p-12 text-center text-zinc-500">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            </div>
          )}

          {job && (
            <div className="border border-zinc-950 bg-white" data-testid="hunt-detail-panel">
              {/* Header */}
              <div className="p-5 border-b border-zinc-200 flex flex-wrap items-start gap-4 justify-between">
                <div className="min-w-0">
                  <div className="label-xs">Job · {job.id.slice(0, 8)}</div>
                  <div className="font-display text-xl font-bold truncate">
                    {job.filename}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">
                    tag: {job.competitor_tag} · min_issues ≥ {job.min_issues}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="border border-zinc-950 px-3 py-1.5">
                    <div className="label-xs">URLs</div>
                    <div className="font-display text-2xl font-black">{job.total_urls}</div>
                  </div>
                  <div className="border border-zinc-950 px-3 py-1.5">
                    <div className="label-xs">Audited</div>
                    <div className="font-display text-2xl font-black">{job.completed}</div>
                  </div>
                  <div className="border border-zinc-950 px-3 py-1.5 bg-emerald-50">
                    <div className="label-xs text-emerald-700">Qualified</div>
                    <div className="font-display text-2xl font-black text-emerald-700" data-testid="hunt-qualified-count">
                      {job.qualified}
                    </div>
                  </div>
                  <div className="border border-zinc-950 px-3 py-1.5 bg-red-50">
                    <div className="label-xs text-red-700">Failed</div>
                    <div className="font-display text-2xl font-black text-red-600">
                      {job.failed}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 pt-3 pb-2 flex items-center gap-3" role="status" aria-live="polite" aria-atomic="true">
                <StatusBadge status={job.status} />
                <div className="flex-1">
                  <ProgressBar completed={job.completed} total={job.total_urls} />
                </div>
                <span className="font-mono text-xs text-zinc-700 tabular-nums" aria-label={`Progress ${pctDone} percent`}>
                  {pctDone}%
                </span>
              </div>

              {job.error && (
                <div className="mx-5 my-3 bg-red-50 border border-red-300 p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
                  Job error: {job.error}
                </div>
              )}

              {/* Results table */}
              <div className="px-5 py-4">
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  {(() => {
                    const qualified = results.filter((r) => r.status === "DONE" && (r.total_issues || 0) >= (job.min_issues || 0));
                    const failed = results.filter((r) => r.status !== "DONE");
                    const lowCount = results.filter((r) => r.status === "DONE" && (r.total_issues || 0) < (job.min_issues || 0));
                    return (
                      <div className="min-w-0">
                        <div className="label-xs">
                          {showNonQualified
                            ? `Results (${results.length}) · ${qualified.length} qualified · ${lowCount.length} low-count · ${failed.length} failed`
                            : `Qualified targets (${qualified.length})`}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5" data-testid="hunt-counts-breakdown">
                          Job stats · URLs {job.total_urls} · audited {job.completed} · qualified {job.qualified} · failed {job.failed}
                          {job.qualified !== qualified.length && !showNonQualified && (
                            <span className="ml-2 text-amber-700" title="On-screen list may differ from the historical counter if a previous run recorded audits with the same competitor_hunt_job_id.">
                              (list = {qualified.length})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex gap-2 flex-wrap items-center">
                    <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showNonQualified}
                        onChange={(e) => setShowNonQualified(e.target.checked)}
                        data-testid="hunt-show-non-qualified"
                        className="rounded-none"
                      />
                      Show non-qualified
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => deleteJob(job.id)}
                      className="rounded-sm h-8 text-[10px] font-bold uppercase tracking-wider border-zinc-400"
                      data-testid="hunt-delete-job-button"
                    >
                      <Trash2 className="w-3 h-3 mr-1" aria-hidden="true" /> Delete Job
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => purgeJob(job.id)}
                      className="rounded-sm h-8 text-[10px] font-bold uppercase tracking-wider border-red-400 text-red-700 hover:bg-red-50"
                      data-testid="hunt-purge-job-button"
                    >
                      <Trash2 className="w-3 h-3 mr-1" aria-hidden="true" /> Purge + Audits
                    </Button>
                  </div>
                </div>

                {/* Stage-4 · selection toolbar */}
                {results.length > 0 && (
                  <div
                    className="bg-zinc-900 text-white px-3 py-2 flex items-center gap-3 flex-wrap mb-2"
                    data-testid="hunt-selection-toolbar"
                    role="region"
                    aria-label="Bulk send selection"
                  >
                    <div className="font-mono text-xs text-zinc-200" data-testid="hunt-selection-summary">
                      <span data-testid="hunt-shown-count">{results.length}</span> shown ·{" "}
                      <span className="text-white font-bold" data-testid="hunt-selected-count">{selectedResultIds.size}</span> selected
                      {ineligibleCount > 0 && (
                        <span className="ml-2 text-[10px] text-zinc-400" title="Rows without a discovered email or already pitched cannot be selected">
                          ({ineligibleCount} not selectable — no email or already pitched)
                        </span>
                      )}
                    </div>
                    {someEligibleSelected && eligibleResultIds.length > selectedEligible.length && (
                      <button
                        type="button"
                        onClick={() => setSelectedResultIds(new Set(eligibleResultIds))}
                        className="text-[10px] font-bold uppercase tracking-wider text-blue-300 underline hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="hunt-select-all-matching"
                      >
                        Select all {eligibleResultIds.length} matching results
                      </button>
                    )}
                    {selectedResultIds.size > 0 && (
                      <button
                        type="button"
                        onClick={clearResultSelection}
                        className="text-[10px] font-bold uppercase tracking-wider text-zinc-300 underline hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="hunt-clear-selection"
                      >
                        Clear selection
                      </button>
                    )}
                    <div className="ml-auto">
                      <Button
                        type="button"
                        onClick={bulkSendPitches}
                        disabled={selectedResultIds.size === 0 || bulkSending}
                        className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-xs h-8 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="hunt-bulk-send-button"
                        aria-label={`Send pitches to ${selectedResultIds.size} selected site${selectedResultIds.size === 1 ? "" : "s"}`}
                      >
                        {bulkSending ? (
                          <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" /> Sending…</>
                        ) : (
                          <><Send className="w-3 h-3 mr-1.5" aria-hidden="true" /> Send pitches to {selectedResultIds.size} site{selectedResultIds.size === 1 ? "" : "s"}</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {results.length === 0 && (
                  <div className="border border-dashed border-zinc-300 py-8 text-center text-xs text-zinc-500">
                    {job.status === "COMPLETED" ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-zinc-400 mx-auto mb-1" aria-hidden="true" />
                        No targets met the threshold. Try lowering min_issues.
                      </>
                    ) : (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" aria-hidden="true" />
                        Auditing in progress — qualified targets will appear here as they finish.
                      </>
                    )}
                  </div>
                )}

                {results.length > 0 && (
                  <div className="overflow-x-auto border border-zinc-200">
                    <table className="w-full text-sm" data-testid="hunt-results-table">
                      <caption className="sr-only">Qualified competitor targets with per-site accessibility issues and outreach action.</caption>
                      <thead className="bg-zinc-100">
                        <tr className="text-left">
                          <th scope="col" className="px-3 py-2 w-10">
                            <Checkbox
                              checked={allEligibleSelected ? true : (someEligibleSelected ? "indeterminate" : false)}
                              onCheckedChange={toggleResultSelectAll}
                              disabled={eligibleResultIds.length === 0}
                              aria-label={allEligibleSelected ? "Deselect all eligible results" : `Select all ${eligibleResultIds.length} eligible results`}
                              className="rounded-none border-zinc-950 data-[state=checked]:bg-zinc-950 data-[state=checked]:text-white"
                              data-testid="hunt-select-all-checkbox"
                            />
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider">
                            URL
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-24">
                            Row status
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-16">
                            Issues
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-16">
                            High
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-48">
                            Email
                          </th>
                          <th scope="col" className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider w-48">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((r) => {
                          const state = sendingState[r.id];
                          const alreadyPitched = r.pitch_status === "PITCHED" || state === "sent";
                          const eligible = isResultEligible(r);
                          const checked = selectedResultIds.has(r.id);
                          const rowStatus = r.status !== "DONE"
                            ? { label: "FAILED", cls: "bg-red-600 text-white" }
                            : (r.total_issues || 0) < (job.min_issues || 0)
                              ? { label: "LOW-COUNT", cls: "bg-zinc-400 text-zinc-950" }
                              : { label: "QUALIFIED", cls: "bg-emerald-600 text-white" };
                          return (
                            <tr
                              key={r.id}
                              className={`border-t border-zinc-100 hover:bg-zinc-50 align-top ${checked ? "bg-blue-50/60" : ""} ${rowStatus.label !== "QUALIFIED" ? "opacity-70" : ""}`}
                              data-testid={`hunt-result-row-${r.id}`}
                            >
                              <td className="px-3 py-2">
                                <Checkbox
                                  checked={checked}
                                  disabled={!eligible}
                                  onCheckedChange={() => toggleResultSelect(r.id)}
                                  aria-label={`Select ${r.target_url}`}
                                  title={eligible ? "Select for bulk send" : (!r.discovered_email ? "No discovered email" : (rowStatus.label !== "QUALIFIED" ? `Row is ${rowStatus.label}` : "Already pitched"))}
                                  className="rounded-none border-zinc-950 data-[state=checked]:bg-zinc-950 data-[state=checked]:text-white"
                                  data-testid={`hunt-result-select-${r.id}`}
                                />
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">
                                <a
                                  href={r.target_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  {r.target_url}
                                  <ExternalLink className="w-3 h-3 opacity-60 shrink-0" aria-hidden="true" />
                                </a>
                              </td>
                              <td className="px-3 py-2">
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${rowStatus.cls}`} data-testid={`hunt-row-status-${r.id}`}>
                                  {rowStatus.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 font-mono font-bold tabular-nums">
                                {r.total_issues}
                              </td>
                              <td className="px-3 py-2 font-mono text-red-700 font-bold tabular-nums">
                                {r.high_severity_count}
                              </td>
                              <td className="px-3 py-2 font-mono text-[11px]">
                                {r.discovered_email ? (
                                  <span
                                    className="inline-flex items-center gap-1 text-zinc-800"
                                    data-testid={`hunt-email-${r.id}`}
                                    title={r.discovered_email}
                                  >
                                    <Mail className="w-3 h-3 text-emerald-700 shrink-0" aria-hidden="true" />
                                    <span className="truncate max-w-[180px]">{r.discovered_email}</span>
                                  </span>
                                ) : (
                                  <span className="text-zinc-400 italic">no email found</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-col gap-1.5">
                                  <Button
                                    type="button"
                                    onClick={() => sendPitchInline(r)}
                                    disabled={!r.discovered_email || state === "sending" || alreadyPitched}
                                    className={`h-7 px-2 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                                      alreadyPitched
                                        ? "bg-emerald-600 hover:bg-emerald-600 text-white cursor-default"
                                        : "bg-blue-700 hover:bg-blue-800 text-white"
                                    } disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                    data-testid={`hunt-send-pitch-${r.id}`}
                                    aria-label={alreadyPitched ? "Already pitched" : `Send pitch to ${r.target_url}`}
                                  >
                                    {state === "sending" ? (
                                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" /> Sending…</>
                                    ) : alreadyPitched ? (
                                      <><Check className="w-3 h-3 mr-1" aria-hidden="true" /> Pitched</>
                                    ) : (
                                      <><Send className="w-3 h-3 mr-1" aria-hidden="true" /> Send Pitch</>
                                    )}
                                  </Button>
                                  <a
                                    href={`/operator/auditor?audit=${r.id}`}
                                    className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    data-testid={`hunt-open-audit-${r.id}`}
                                    aria-label={`View findings for ${r.target_url}`}
                                  >
                                    View findings →
                                  </a>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
